import React from 'react';
import ReactDOM from 'react-dom/client';
import { FluentProvider, useId } from '@fluentui/react-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { darkTheme, lightTheme } from './theme';
import { AuthProvider } from './auth';
import { App } from './App';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } } });

/** Follows the computer's light or dark setting. */
function useDark() {
  const [dark, setDark] = React.useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  React.useEffect(() => {
    const m = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setDark(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return dark;
}

function Root() {
  const dark = useDark();
  useId(); // keeps the hook order stable across StrictMode double renders
  // The provider's min-height is an inline style on purpose: Fluent copies a provider's className onto the layer it draws menus and
  // dialogs in, so a class here would stretch that layer over the page.
  return (
    <FluentProvider theme={dark ? darkTheme : lightTheme} style={{ minHeight: '100vh' }}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </FluentProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
